import React from 'react';
import styled, { keyframes, css } from 'styled-components';

const bounceInBefore = keyframes`
  0% { opacity: 0; transform: scale(0.3) translate(-50%, -50%) rotate(45deg); }
  50% { opacity: 0.9; transform: scale(1.1) translate(-50%, -50%) rotate(45deg); }
  80% { opacity: 1; transform: scale(0.89) translate(-50%, -50%) rotate(45deg); }
  100% { opacity: 1; transform: scale(1) translate(-50%, -50%) rotate(45deg); }
`;

const bounceInAfter = keyframes`
  0% { opacity: 0; transform: scale(0.3) translate(-50%, -50%) rotate(-45deg); }
  50% { opacity: 0.9; transform: scale(1.1) translate(-50%, -50%) rotate(-45deg); }
  80% { opacity: 1; transform: scale(0.89) translate(-50%, -50%) rotate(-45deg); }
  100% { opacity: 1; transform: scale(1) translate(-50%, -50%) rotate(-45deg); }
`;

const bounceOut = (deg: number) => keyframes`
  0% { opacity: 1; transform: scale(1) translate(-50%, -50%) rotate(${deg}deg); }
  100% { opacity: 0; transform: scale(0.3) translate(-50%, -50%) rotate(${deg}deg); }
`;

const Wrapper = styled.div`
  * {
    box-sizing: border-box;
  }
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  display: none;
  visibility: hidden;
`;

const ToggleButton = styled.label`
  cursor: pointer;
  display: block;
  transform-origin: 50% 50%;
  transform-style: preserve-3d;
  transition: transform 0.14s ease;

  &:active {
    transform: rotateX(30deg);
  }
`;

const Circle = styled.div<{ checked: boolean }>`
  border: ${({ checked }) => ( checked ? '3px solid rgba(82, 167, 167, 0.5)' : '3px solid rgba(240, 75, 96, 0.2)' )};
  border-radius: 50%;
  width: 25px;
  height: 25px;
  position: relative;
`;

const Svg = styled.svg<{ checked: boolean }>`
  fill: none;
  stroke-width: 4;
  stroke: ${({ checked }) => ( checked ? '#52C5A7' : '#F04B60' )};
  stroke-linecap: round;
  stroke-linejoin: round;
  width: 25px;
  height: 25px;
  position: absolute;
  left: -3px;
  top: -3px;
  z-index: 1;
  stroke-dashoffset: ${({ checked }) => (checked ? '162.6' : '124.6')};
  stroke-dasharray: ${({ checked }) =>
    checked ? '0 162.6 28 134.6' : '0 162.6 133 29.6'};
  transition: all 0.4s ease;
  transition-delay: ${({ checked }) => (checked ? '0.2s' : '0s')};
`;

const Line = styled.div<{ checked: boolean; rotate: number }>`
  content: '';
  width: 2px;
  height: 16px;
  background: #F04B60;
  position: absolute;
  left: 50%;
  top: 50%;
  border-radius: 5px;
  opacity: 0;
  transform: scale(0.3) translate(-50%, -50%) rotate(${({ rotate }) => rotate}deg);

  ${({ checked, rotate }) =>
    checked
      ? css`
          animation: ${bounceOut(rotate)} 0.3s linear forwards;
        `
      : css`
          animation: ${rotate === 45 ? bounceInBefore : bounceInAfter} 0.3s linear forwards 0.3s;
        `}
`;

type Props = {
  checked: boolean;
  onChange: () => void;
  id: string;
};

const BounceCheckbox: React.FC<Props> = ({ checked, onChange, id }) => {
  return (
    <Wrapper>
      <HiddenCheckbox id={id} checked={checked} onChange={onChange} />
      <ToggleButton htmlFor={id}>
        <Circle checked={checked}>
          <Svg viewBox="0 0 44 44" checked={checked}>
            <path d="M14,24 L21,31 L39.7428882,11.5937758 C35.2809627,6.53125861 30.0333333,4 24,4 C12.95,4 4,12.95 4,24 C4,35.05 12.95,44 24,44 C35.05,44 44,35.05 44,24 C44,19.3 42.5809627,15.1645919 39.7428882,11.5937758" transform="translate(-2.000000, -2.000000)"/>
          </Svg>
          <Line checked={checked} rotate={45} />
          <Line checked={checked} rotate={-45} />
        </Circle>
      </ToggleButton>
    </Wrapper>
  );
};

export default BounceCheckbox;
